import { useCallback, useMemo } from 'react';
import { isSameOrAfterToday } from '../utils/helpers/date.helper';

import { useAppData } from '../context';
import { measures } from '../utils/helpers/measures.helpers';
import {
  PELAGOS_RMC,
  PLUTON_RMC,
  REQUIRED_TRANSLATION_TYPES,
  SPECIAL_EDITION_DECOR_KEY,
  CATEGORY_C,
  METAS_RMCS,
  LIGHT_THEME_DISABLED_RMCS,
} from '../utils/helpers/constants.helper';

export const useWatchProperties = () => {
  const { watchData, businessData } = useAppData();

  const modelCharacteristics = businessData?.[0]?.ModelCharacteristics;
  const componentCategory = modelCharacteristics?.ComponentCategory;
  const translations = businessData?.[0]?.Translations;
  const rawRmc = watchData?.Configuration?.Rmc;
  const rmc = rawRmc?.toUpperCase();
  const isCategoryC = componentCategory?.toUpperCase() === CATEGORY_C;
  const diamondsSpec = Array.isArray(businessData)
    ? businessData[0]?.Specspecification?.[0]?.Brilliants
    : undefined;

  const isMetas = useMemo(
    () =>
      Boolean(rmc && (METAS_RMCS.has(rmc) || measures(watchData?.Measures))),
    [rmc, watchData?.Measures],
  );

  const shouldEnableLightTheme = useMemo(
    () => !rmc || !LIGHT_THEME_DISABLED_RMCS.has(rmc),
    [rmc],
  );

  const isUnderWarranty = useCallback((warrantyEndDate) => {
    if (!warrantyEndDate) return false;

    return isSameOrAfterToday(warrantyEndDate);
  }, []);

  const isPluton = useMemo(() => PLUTON_RMC.has(rmc), [rmc]);
  const isPelagos = useMemo(() => PELAGOS_RMC.has(rmc), [rmc]);

  const hasFullSpecifications = useMemo(() => {
    if (!Array.isArray(translations) || translations.length === 0 || !rmc) {
      return false;
    }

    const availableTypes = new Set(
      translations
        .filter((translation) => translation?.Type && translation?.Translation)
        .map((translation) => translation.Type),
    );

    for (const type of REQUIRED_TRANSLATION_TYPES) {
      if (!availableTypes.has(type)) {
        return false;
      }
    }

    return true;
  }, [translations, rmc]);

  const shouldEnableWatchTab = isMetas || hasFullSpecifications;

  const isSpecialEdition = useCallback(
    (data = businessData) => {
      if (!Array.isArray(data)) return false;

      return data.some((item) =>
        item?.Specspecification?.some((specification) =>
          specification?.SpecsComponent?.some(
            (component) => component?.Decor_key === SPECIAL_EDITION_DECOR_KEY,
          ),
        ),
      );
    },
    [businessData],
  );

  return {
    rmc,
    isMetas,
    shouldEnableLightTheme,
    hasFullSpecifications,
    isCategoryC,
    isUnderWarranty,
    isPluton,
    isPelagos,
    shouldEnableWatchTab,
    isSpecialEdition,
    diamondsSpec, // {"count": number, "carats": string}
  };
};
